import { ConfigProvider as AntdConfigProvider } from 'antd';
import frFR from 'antd/locale/fr_FR';
import type { Config as DriverConfig } from 'driver.js';
import React, { lazy, Suspense, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
import { PersistGate } from 'redux-persist/integration/react';
import { Route, Routes } from 'react-router-dom';

import AppLayout from '~/layouts/AppLayout';

import Profile from '~/routes/Profile';
import RequireAuth from '~/routes/RequireAuth';

import DocumentationContext from '~/hooks/DocumentationContext';

import { history, persistor, store } from '~/redux/store';

const Call = lazy(async () => await import('~/routes/shared/Call'));
const CampaignsEdit = lazy(async () => await import('~/routes/campaigns/Edit'));
const CampaignsIndex = lazy(async () => await import('~/routes/campaigns/Index'));
const CampaignsNew = lazy(async () => await import('~/routes/campaigns/New'));
const CampaignsNext = lazy(async () => await import('~/routes/campaigns/Next'));
const CampaignsSingle = lazy(async () => await import('~/routes/campaigns/Single'));
const Changelog = lazy(async () => await import('~/routes/Changelog'));
const ContactsImport = lazy(async () => await import('~/routes/contacts/Import'));
const ContactsIndex = lazy(async () => await import('~/routes/contacts/Index'));
const ContactsSingle = lazy(async () => await import('~/routes/contacts/Single'));
const EmailTemplatesEdit = lazy(async () => await import('~/routes/emailTemplates/Edit'));
const EmailTemplatesIndex = lazy(async () => await import('~/routes/emailTemplates/Index'));
const EmailTemplatesNew = lazy(async () => await import('~/routes/emailTemplates/New'));
const EmailTemplatesSingle = lazy(async () => await import('~/routes/emailTemplates/Single'));
const GroupsEdit = lazy(async () => await import('~/routes/groups/Edit'));
const GroupsImport = lazy(async () => await import('~/routes/groups/Import'));
const GroupsIndex = lazy(async () => await import('~/routes/groups/Index'));
const GroupsNew = lazy(async () => await import('~/routes/groups/New'));
const GroupsNext = lazy(async () => await import('~/routes/groups/Next'));
const GroupsSingle = lazy(async () => await import('~/routes/groups/Single'));
const Home = lazy(async () => await import('~/routes/Home'));
const NotFound = lazy(async () => await import('~/routes/NotFound'));
const Reports = lazy(async () => await import('~/routes/Reports'));
const RolesEdit = lazy(async () => await import('~/routes/roles/Edit'));
const RolesIndex = lazy(async () => await import('~/routes/roles/Index'));
const RolesNew = lazy(async () => await import('~/routes/roles/New'));
const RolesSingle = lazy(async () => await import('~/routes/roles/Single'));
const SourceEmailsEdit = lazy(async () => await import('~/routes/sourceEmails/Edit'));
const SourceEmailsIndex = lazy(async () => await import('~/routes/sourceEmails/Index'));
const SourceEmailsNew = lazy(async () => await import('~/routes/sourceEmails/New'));
const SourceEmailsSingle = lazy(async () => await import('~/routes/sourceEmails/Single'));
const SourcePhoneNumbersEdit = lazy(async () => await import('~/routes/sourcePhoneNumbers/Edit'));
const SourcePhoneNumbersIndex = lazy(async () => await import('~/routes/sourcePhoneNumbers/Index'));
const SourcePhoneNumbersNew = lazy(async () => await import('~/routes/sourcePhoneNumbers/New'));
const SourcePhoneNumbersSingle = lazy(async () => await import('~/routes/sourcePhoneNumbers/Single'));
const TagsEdit = lazy(async () => await import('~/routes/tags/Edit'));
const TagsIndex = lazy(async () => await import('~/routes/tags/Index'));
const TagsNew = lazy(async () => await import('~/routes/tags/New'));
const TagsSingle = lazy(async () => await import('~/routes/tags/Single'));
const UsersEdit = lazy(async () => await import('~/routes/users/Edit'));
const UsersImport = lazy(async () => await import('~/routes/users/Import'));
const UsersIndex = lazy(async () => await import('~/routes/users/Index'));
const UsersNew = lazy(async () => await import('~/routes/users/New'));
const UsersSingle = lazy(async () => await import('~/routes/users/Single'));

export default function App(): JSX.Element {
  const [documentation, setDocumentation] = useState<Record<string, DriverConfig>>({});
  const [running, setRunning] = useState<boolean>(false);
  const documentationValue = {
    documentation,
    running,
    setDocumentation,
    setRunning,
  };

  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Router history={history}>
          <AntdConfigProvider locale={frFR}>
            <DocumentationContext.Provider value={documentationValue}>
              <Suspense fallback={<AppLayout />}>
                <Routes>
                  <Route element={<AppLayout />}>
                    <Route index element={<Home />} />
                    <Route path='/profile' element={<Profile />} />
                    <Route path='/reports' element={<Reports />} />
                    <Route path='/campaigns'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <CampaignsIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <CampaignsNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <CampaignsEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/calls/next'
                        element={
                          <RequireAuth>
                            <CampaignsNext />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/calls/:callId'
                        element={
                          <RequireAuth>
                            <Call />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <CampaignsSingle />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/:tab'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <CampaignsSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/contacts'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <ContactsIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='import'
                        element={
                          <RequireAuth roles={['ContactsImport']} redirectTo='/contacts'>
                            <ContactsImport />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <ContactsSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/configuration/email_templates'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin']}>
                            <EmailTemplatesIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <EmailTemplatesNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <EmailTemplatesEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <EmailTemplatesSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/configuration/source_emails'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourceEmailsIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourceEmailsNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourceEmailsEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourceEmailsSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/configuration/source_phone_numbers'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourcePhoneNumbersIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourcePhoneNumbersNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourcePhoneNumbersEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <SourcePhoneNumbersSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/groups'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <GroupsIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='import'
                        element={
                          <RequireAuth roles={['GroupsImport']} redirectTo='/groups'>
                            <GroupsImport />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <GroupsNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <GroupsEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/calls/next'
                        element={
                          <RequireAuth>
                            <GroupsNext />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/calls/:callId'
                        element={
                          <RequireAuth>
                            <Call />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <GroupsSingle />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/:tab'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <GroupsSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/roles'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin']}>
                            <RolesIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <RolesNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <RolesEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <RolesSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/tags'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <TagsIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <TagsNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <TagsEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin', 'Manager']}>
                            <TagsSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/users'>
                      <Route
                        index
                        element={
                          <RequireAuth roles={['Admin']}>
                            <UsersIndex />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='import'
                        element={
                          <RequireAuth roles={['UsersImport']} redirectTo='/users'>
                            <UsersImport />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path='new'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <UsersNew />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id/edit'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <UsersEdit />
                          </RequireAuth>
                        }
                      />
                      <Route
                        path=':id'
                        element={
                          <RequireAuth roles={['Admin']}>
                            <UsersSingle />
                          </RequireAuth>
                        }
                      />
                    </Route>
                    <Route path='/changelog' element={<Changelog />} />
                    <Route path='/*' element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
            </DocumentationContext.Provider>
          </AntdConfigProvider>
        </Router>
      </PersistGate>
    </ReduxProvider>
  );
}
